import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { AlertCircle, MapPin, Phone, X, Plus, Eye } from "lucide-react";

export default function LostPets() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => axios.get(`${API}/lost-pets`).then(r => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="lost-pets-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Lost & Found</h1>
          <p className="text-ink-soft">Help reunite lost pets with their families.</p>
        </div>
        <button data-testid="report-lost-button" onClick={() => setOpen(true)} className="bg-danger text-white rounded-full px-5 py-2.5 font-bold inline-flex items-center gap-2 hover:opacity-90">
          <AlertCircle className="w-4 h-4"/> Report a lost pet
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map(p => <LostPetCard key={p.report_id} p={p} onUpdate={load}/>)}
        {items.length === 0 && <div className="text-ink-soft">No lost pets reported. Hooray! 🎉</div>}
      </div>

      {open && <ReportModal onClose={() => { setOpen(false); load(); }}/>}
    </div>
  );
}

function LostPetCard({ p, onUpdate }) {
  const [sighting, setSighting] = useState(false);
  return (
    <article data-testid={`lost-${p.report_id}`} className="bg-surface border border-danger/30 rounded-3xl overflow-hidden shadow-soft">
      {p.photo && <img src={p.photo} alt={p.pet_name} className="w-full h-44 object-cover"/>}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold uppercase tracking-wider bg-danger text-white rounded-full px-2 py-1">Lost</span>
          <h3 className="font-heading text-xl font-extrabold">{p.pet_name}</h3>
        </div>
        <div className="text-xs text-ink-soft uppercase tracking-wider font-bold">{p.species}{p.breed ? ` · ${p.breed}` : ""}</div>
        <p className="text-sm text-ink-soft mt-2 line-clamp-3">{p.description}</p>
        <div className="mt-2 text-xs text-ink-soft space-y-1">
          <div className="flex items-center gap-1"><MapPin className="w-3 h-3"/>{p.last_seen_location}</div>
          <div className="flex items-center gap-1"><Phone className="w-3 h-3"/>{p.contact_phone}</div>
        </div>
        {p.sightings?.length > 0 && (
          <div className="mt-3 text-xs bg-leaf/20 rounded-2xl p-2"><Eye className="w-3 h-3 inline mr-1"/>{p.sightings.length} sighting{p.sightings.length > 1 ? "s" : ""}</div>
        )}
        <button data-testid={`sight-${p.report_id}`} onClick={() => setSighting(true)} className="mt-4 w-full bg-ink text-white rounded-full py-2.5 text-sm font-bold hover:bg-brand">
          I've seen this pet
        </button>
      </div>
      {sighting && <SightingModal report={p} onClose={() => { setSighting(false); onUpdate(); }}/>}
    </article>
  );
}

function ReportModal({ onClose }) {
  const [form, setForm] = useState({ pet_name: "", species: "dog", breed: "", last_seen_location: "", description: "", contact_phone: "", photo: "" });
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const last_seen_at = new Date().toISOString();
    await axios.post(`${API}/lost-pets/report`, { ...form, last_seen_at });
    setDone(true);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md border border-line shadow-soft" data-testid="lost-report-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-extrabold">Report a lost pet</h3>
          <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-surface-muted"><X className="w-4 h-4"/></button>
        </div>
        {!done ? (
          <div className="space-y-3">
            <F label="Pet name"><input required value={form.pet_name} onChange={e=>setForm({...form, pet_name:e.target.value})} className="li"/></F>
            <F label="Species">
              <select value={form.species} onChange={e=>setForm({...form, species:e.target.value})} className="li">
                {["dog","cat","bird","rabbit","other"].map(s => <option key={s}>{s}</option>)}
              </select>
            </F>
            <F label="Breed (optional)"><input value={form.breed} onChange={e=>setForm({...form, breed:e.target.value})} className="li"/></F>
            <F label="Last seen location"><input required value={form.last_seen_location} onChange={e=>setForm({...form, last_seen_location:e.target.value})} className="li" placeholder="Park, neighbourhood, city"/></F>
            <F label="Description"><textarea required rows={3} value={form.description} onChange={e=>setForm({...form, description:e.target.value})} className="li resize-none"/></F>
            <F label="Photo URL (optional)"><input value={form.photo} onChange={e=>setForm({...form, photo:e.target.value})} className="li"/></F>
            <F label="Your phone"><input required value={form.contact_phone} onChange={e=>setForm({...form, contact_phone:e.target.value})} className="li"/></F>
            <button data-testid="lost-submit" className="w-full bg-danger text-white rounded-full py-3 font-bold mt-2">Post lost-pet report</button>
          </div>
        ) : (
          <div className="text-center py-6"><AlertCircle className="w-12 h-12 text-danger mx-auto mb-2"/><div className="font-heading text-xl font-extrabold">Report posted</div><p className="text-sm text-ink-soft mt-1">The community will be notified. Fingers crossed!</p></div>
        )}
        <style>{`.li{width:100%;border:1px solid #EAE0D5;border-radius:1.5rem;padding:0.6rem 1rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.li:focus{border-color:#FF6B35;}`}</style>
      </form>
    </div>
  );
}

function SightingModal({ report, onClose }) {
  const [form, setForm] = useState({ note: "", location: "" });
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    let lat, lng;
    try {
      const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 4000 }));
      lat = pos.coords.latitude; lng = pos.coords.longitude;
    } catch {}
    await axios.post(`${API}/lost-pets/sighting`, { report_id: report.report_id, ...form, lat, lng });
    setDone(true);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md border border-line shadow-soft" data-testid="sighting-form">
        <h3 className="font-heading text-xl font-extrabold mb-3">Report sighting of {report.pet_name}</h3>
        {!done ? (
          <div className="space-y-3">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Where</span>
              <input required value={form.location} onChange={e=>setForm({...form, location:e.target.value})} className="li" placeholder="Cross-street, landmark"/>
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Note</span>
              <textarea required rows={3} value={form.note} onChange={e=>setForm({...form, note:e.target.value})} className="li resize-none" placeholder="What did you see? Was the pet alone? Direction of travel?"/>
            </label>
            <button data-testid="sighting-submit" className="w-full bg-ink text-white rounded-full py-3 font-bold">Share sighting</button>
            <p className="text-xs text-ink-soft text-center">We'll attach your approximate location to help the owner.</p>
          </div>
        ) : (
          <div className="text-center py-6"><Eye className="w-12 h-12 text-leaf mx-auto mb-2"/><div className="font-heading text-xl font-extrabold">Sighting shared</div><p className="text-sm text-ink-soft mt-1">The owner has been notified.</p><button type="button" onClick={onClose} className="mt-4 bg-ink text-white rounded-full px-5 py-2 text-sm font-bold">Close</button></div>
        )}
        <style>{`.li{width:100%;border:1px solid #EAE0D5;border-radius:1.5rem;padding:0.6rem 1rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.li:focus{border-color:#FF6B35;}`}</style>
      </form>
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
